/* eslint-disable @typescript-eslint/no-empty-function */
import { FunctionComponent, PropsWithChildren, useState } from "react";

import { TicketingFormCategory, PaymentMethod, StripeRecurringInterval, getCurrencyFromCountry } from "@simplyk/common";

import { useComputeTip } from "../../../../components/PaymentProcessor/hooks/useComputeTip";
import { FrontendFormContext, FrontendFormContextValues } from "../../../../contexts/FrontendFormContext";
import { FormType } from "../../../../gql/gql-types";

import { FrontendDonationFormContext, FrontendDonationFormContextValues } from "@/contexts/FrontendDonationFormContext";
import { DonationFormFrontendPageOutput } from "@/types/trpc";

interface DonationPreviewProviderProps {
  donationForm: DonationFormFrontendPageOutput;
}

export const PREVIEW_DISPLAYED_FORM_AMOUNT = 100_00 as const;

/**
 * This component provides dedicated instances of FrontendFormContext.Provider
 * and FrontendTicketingContext.Provider for the live preview.
 *
 * Unlike the contexts used in normal form pages, these dedicated providers are
 * tailored for preview functionality and use mock data or placeholder values.
 */
export const DonationFrontendPreviewProvider: FunctionComponent<PropsWithChildren<DonationPreviewProviderProps>> = ({
  children,
  donationForm,
}) => {
  const organization = donationForm.organization;
  const currency = getCurrencyFromCountry(organization.country);

  const previewSelectedPaymentMethod = {
    paymentMethod: PaymentMethod.Card,
  };

  const { tip, suggestedTips, selectedTip, isSuggestedTipsAmount, freeTips, fees, formattedFees, getTipChoice } =
    useComputeTip({
      displayedFormAmount: PREVIEW_DISPLAYED_FORM_AMOUNT,
      currency,
      organizationCountry: organization?.country,
      paymentMethod: previewSelectedPaymentMethod.paymentMethod,
      isAuction: false,
      category: donationForm.category,
      formData: donationForm,
    });

  const [stripeRecurrenceInterval, setStripeRecurrenceInterval] = useState<StripeRecurringInterval | null>(null);

  const frontendFormContextValues: FrontendFormContextValues = {
    formData: donationForm,
    organization,
    commandId: "",
    formType: FormType.Ticketing,
    category: TicketingFormCategory.Event,
    translationLocales: [],
    isOnboarding: false,
    field: undefined,
    isEmbed: false,
    isFundraiser: false,
    isFormValid: false,
    setIsFormValid: () => {},
    isFormDirty: false,
    setIsFormDirty: () => {},
    shouldDisplayLogin: false,
    shouldDisplayLanguageSelect: false,
    formAmplitudeContext: {} as never,
    selectedPaymentMethod: undefined,
    setSelectedPaymentMethod: () => {},
    currency,
    displayedFormAmount: PREVIEW_DISPLAYED_FORM_AMOUNT,
    setDisplayedFormAmount: () => {},
    displayedFormAmountWithTip: PREVIEW_DISPLAYED_FORM_AMOUNT + tip,
    tip,
    suggestedTips,
    selectedTip,
    isSuggestedTipsAmount,
    handleChangeTips: () => {},
    handleChangeFreeTips: () => {},
    resetTipToCoverBankCharge: () => {},
    freeTips,
    updateFreeTipAmount: () => {},
    fees,
    formattedFees,
    displayTipSelection: true,
    stripeRecurrenceInterval,
    setStripeRecurrenceInterval,
    isMembershipV2: false,
    isArchived: false,
    paymentRequest: null,
    setPaymentRequest: () => {},
    canUsePaymentRequest: false,
    setCanUsePaymentRequest: () => {},
    areBankPaymentMethodsAllowed: false,
    allowedPaymentMethods: [],
    postTransactionUrl: null,
    prioritizeBankPayment: false,
    selectedPaymentMethodIsBank: false,
    selectedPaymentMethodIsCard: false,
    chequeDescription: null,
    showProceedButton: false,
    displayExpressCheckout: false,
    handleSetSupportsExpressCheckout: () => {},
    isExpressCheckoutLoading: false,
    isSubmitting: false,
    setIsSubmitting: () => {},
    expressCheckoutIsSelected: false,
    showTopBillingInfo: false,
    showBottomBillingInfo: false,
    expressCheckoutDisabled: false,
    paymentMode: "payment" as const,
    isAuction: false,
    shouldSendTipPercentage: false,
    getTipChoice,
    logAmplitudeTipsChanged: () => {},
    displayAddressQuestion: donationForm.askForAddress,
    askForAddress: donationForm.askForAddress,
    isPreviewTemplateMode: false,
    isPreview: true,
    generateETicket: false,
    defaultSelectedPaymentMethod: previewSelectedPaymentMethod.paymentMethod,
    isFreeTicketing: false,
    isPaymentDisabled: false,
    isPadLoading: false,
    isVerifiedByZeffyBadge: false,
    setIsPadLoading: () => {},
    livePeerCredibilityChipVariant: false,
    formCtaCopyResult: false,
  };

  const frontendDonationFormContextValues: FrontendDonationFormContextValues = {
    donationForm,
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    donationFormField: donationForm.donationFormFields?.[0]!,
    areBankPaymentMethodsAllowed: false,
  };

  return (
    <FrontendFormContext.Provider value={frontendFormContextValues}>
      <FrontendDonationFormContext.Provider value={frontendDonationFormContextValues}>
        {children}
      </FrontendDonationFormContext.Provider>
    </FrontendFormContext.Provider>
  );
};
